// Catalog.ID Client — how many pages a document set is
// ============================================================================
// The Terms step asks whether a printer is available; a printer that runs out
// halfway leaves a member without half their credentials, so the question names
// the size (staging test 2026-10-15). There it can only be "about": the recovery
// pattern and its copies are chosen later. The print step has the document in
// hand and says the exact number.

import type { Content } from 'pdfmake/interfaces';
import type { BookletPrintOptions } from './booklet';
import type { CopySheet } from './booklet-definition';
import { DELEGATE_TERMS_ASSET, ORGANIZATION_TERMS_ASSET, TERMS_ASSET } from '../terms-asset';
import { registrationRecoverySheetCount } from '../recovery-kit-presets';

export type DocumentSetKind = 'personal' | 'organization' | 'delegate';

/* Each kit's one-page sections with every optional sheet present, as its
 * builder lists them — keep in step with those lists:
 *   personal      cover, introduction, credentials, Unlock Sheet, XRPL master
 *                 key, Authenticator Key, Bitcash wallet  (personalKitLayout)
 *   organization  cover, introduction, organization credentials, Bitcash
 *                 wallet  (buildOrgPages)
 *   delegate      cover, introduction, delegate credentials, Unlock Sheet, XRPL
 *                 master key, Authenticator Key, Bitcash wallet  (buildDelegateKit)
 * page-estimate.test.ts pins the totals to what the sample renderer prints. */
const PERSONAL_KIT_PAGES = 7;
const ORGANIZATION_KIT_PAGES = 4;
const DELEGATE_KIT_PAGES = 7;

/** About how many pages a wizard's document set prints, before its recovery
 *  pattern is chosen — the largest one is assumed. An organization registration
 *  prints two kits: the organization's and the founding delegate's own. */
export function estimatedDocumentSetPages(kind: DocumentSetKind): number {
    const delegateKit = DELEGATE_KIT_PAGES + DELEGATE_TERMS_ASSET.pages;
    switch (kind) {
        case 'personal':
            return PERSONAL_KIT_PAGES + registrationRecoverySheetCount('personal') + TERMS_ASSET.pages;
        case 'organization':
            return ORGANIZATION_KIT_PAGES + registrationRecoverySheetCount('organization')
                + ORGANIZATION_TERMS_ASSET.pages + delegateKit;
        case 'delegate':
            return delegateKit;
    }
}

/** The pages a print job sends to the printer — exactly what `printBooklet`
 *  prints for the same arguments: the numbered pages (the agreement included),
 *  then the unnumbered recovery-sheet copies. */
export function documentSetPageCount(
    pages: Content[][], copySheets: CopySheet[], options: BookletPrintOptions = {},
): number {
    const numbered = options.terms
        ? options.totalPages ?? pages.length + options.terms.pages
        : pages.length;
    return numbered + copySheets.length;
}

/** The Terms step's printer question, with the size it is about. */
export function printerCheckboxLabel(kind: DocumentSetKind): string {
    return `I have a printer available to print the document set — about ${estimatedDocumentSetPages(kind)} pages`;
}
